import { C } from "../../lib/colors.js";
import { fmtRelative } from "../../lib/formatDates.js";
import { answerSummary } from "../../lib/forecast/answerSummary.js";
import { listSource } from "../../lib/forecast/listSource.js";
import { questionOf, resolvesText, updatedAtOf } from "../../lib/phone/phoneModel.js";
import { Skeleton, Spinner } from "../ui/Primitives.jsx";
import { IconAlert, IconBell, IconCheck } from "../ui/Icons.jsx";

/** The answer on a card: the live answer, the settled outcome, or what is happening instead. */
function CardAnswer({ f, now }) {
  const a = answerSummary(f, { now });
  const status = a.status;
  if (status?.kind === "running") {
    return (
      <div className="sy-ph__fca">
        <span className="sy-ph__fcstate" style={{ color: C.accent }}>
          <Spinner label="Forecasting" />
          Forecasting
        </span>
      </div>
    );
  }
  if (status?.kind === "error" || status?.kind === "voided") {
    return (
      <div className="sy-ph__fca">
        <span className="sy-ph__fcstate" style={{ color: C.gray }}>
          <IconAlert />
          {status.label}
        </span>
      </div>
    );
  }
  if (status?.kind === "resolved") {
    const outcome = status.label.replace(/^Resolved:?\s*/, "") || "Resolved";
    return (
      <div className="sy-ph__fca">
        <span className="sy-ph__fcl" style={{ color: C.gray }}>
          Resolved
        </span>
        <span className="sy-ph__fcstate" style={{ color: C.gold, maxWidth: 116 }}>
          <IconCheck />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {outcome.charAt(0).toUpperCase() + outcome.slice(1)}
          </span>
        </span>
      </div>
    );
  }
  if (a.kind === "none" || !a.leader) {
    return (
      <div className="sy-ph__fca">
        <span className="sy-ph__fcp sy-num" style={{ color: C.gray }}>
          –
        </span>
      </div>
    );
  }
  return (
    <div
      className="sy-ph__fca"
      aria-label={a.kind === "binary" ? `${a.headlineText} ${a.headlineLabel}` : a.leaderStatement}
    >
      <span className="sy-ph__fcl" style={a.leader.isOther ? { color: C.gray } : undefined}>
        {a.leader.label}
      </span>
      <span className="sy-ph__fcp sy-num">{a.headlineText}</span>
    </div>
  );
}

/** One forecast: question (two lines), when it was updated, and its answer on the right. */
function PhoneForecastCard({ f, active, now, onOpen }) {
  // A lean row draws its answer and dates from the list summary until its detail lands.
  const src = listSource(f);
  const updated = updatedAtOf(src);
  const meta = [
    updated ? `Updated ${fmtRelative(updated, now.getTime())}` : "",
    resolvesText(src, now),
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <button
      type="button"
      className="sy-ph__fc"
      data-ui="phone.forecast-card"
      data-ui-key={f.id}
      aria-current={active ? "true" : undefined}
      onClick={() => onOpen(f)}
    >
      <span className="sy-ph__fcq" data-telemetry-redact="">
        {questionOf(f) || "Untitled question"}
      </span>
      <CardAnswer f={src} now={now} />
      <span className="sy-ph__fcmeta">
        {f.alert ? (
          <span className="sy-ph__moved">
            <IconBell /> Moved
          </span>
        ) : null}
        <span>{meta}</span>
      </span>
    </button>
  );
}

function Section({ title, rows, activeId, now, onOpen }) {
  if (!rows.length) return null;
  return (
    <>
      <div className="sy-ph__lhead">
        <h2 className="sy-ph__h2">{title}</h2>
        <span className="sy-num">{rows.length}</span>
      </div>
      <div className="sy-ph__list">
        {rows.map((f) => (
          <PhoneForecastCard
            key={f.id}
            f={f}
            active={f.id === activeId}
            now={now}
            onOpen={onOpen}
          />
        ))}
      </div>
    </>
  );
}

/** Placeholder cards while the list loads. */
export function PhoneListSkeleton() {
  return (
    <div className="sy-ph__list" style={{ marginTop: 8 }} aria-busy="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="sy-ph__fc" style={{ cursor: "default" }}>
          <div>
            <Skeleton width="92%" height={14} />
            <Skeleton width="64%" height={14} style={{ marginTop: 8 }} />
            <Skeleton width="48%" height={11} style={{ marginTop: 12 }} />
          </div>
          <Skeleton width={44} height={20} />
        </div>
      ))}
    </div>
  );
}

/**
 * The forecast list: Watchlist first, then Recent.
 * @param {{sections: {watchlist: any[], recent: any[]}, activeId: string|null, nowMs: number,
 *   onOpen: (f: any) => void}} props
 */
export default function PhoneForecastList({ sections, activeId, nowMs, onOpen }) {
  const now = new Date(nowMs);
  return (
    <div>
      <Section
        title="Watchlist"
        rows={sections.watchlist}
        activeId={activeId}
        now={now}
        onOpen={onOpen}
      />
      <Section
        title="Recent"
        rows={sections.recent}
        activeId={activeId}
        now={now}
        onOpen={onOpen}
      />
    </div>
  );
}
